/**
 * Bonus / SLA clock.
 *
 * Computes the business-day elapsed time between the first
 * `Submitted → In Progress` transition (the "draw") and the first
 * `… → Awaiting Signature` transition, excluding any periods in
 * `paused`. ≤ 3 business days qualifies for the bonus and counts as
 * SLA-compliant. See CLAUDE.md §21.
 *
 * The authoritative timeline lives in the immutable `file_notes` system
 * events (see fileNotesApi.js). Column timestamps on the `files` row
 * (drawn_at, sent_to_sign_at, paused_total_ms) are used only as a
 * fallback when notes are missing — e.g. very old files from before
 * the file-notes feature was rolled out.
 */

import { businessMillisBetween, BONUS_CLOCK_THRESHOLD_MS } from './businessDays';

const DRAW_PREFIX = 'File drawn by ';
const PAUSE_BODIES = new Set([
  'File paused.',
  'File auto-paused — another file was resumed.',
]);
const RESUME_BODY = 'File resumed.';
const SENT_TO_SIGN_BODY = 'File moved to Awaiting Signature.';

function tsOf(note) {
  return new Date(note.createdAt).getTime();
}

/**
 * First system note whose body matches `predicate(body)`, by ascending
 * createdAt. Returns the note or null. Assumes `notes` is already
 * ordered (the API does this) but the function works on any input.
 */
function firstSystemNote(notes, predicate) {
  if (!notes || notes.length === 0) return null;
  let best = null;
  for (const n of notes) {
    if (n.authorType !== 'system') continue;
    if (!predicate(n.body || '')) continue;
    if (best === null || tsOf(n) < tsOf(best)) best = n;
  }
  return best;
}

/**
 * Walk system notes within [windowStart, windowEnd) and return the
 * pause intervals {start, end} (clipped to the window). An unmatched
 * pause (no resume before windowEnd, e.g. a currently-paused file)
 * is closed at windowEnd.
 */
export function getPauseIntervals(notes, windowStart, windowEnd) {
  if (windowStart == null || windowEnd == null || windowEnd <= windowStart) {
    return [];
  }
  const ws = typeof windowStart === 'number' ? windowStart : new Date(windowStart).getTime();
  const we = typeof windowEnd === 'number' ? windowEnd : new Date(windowEnd).getTime();
  if (we <= ws) return [];

  const intervals = [];
  let openStart = null;

  // Iterate in createdAt order. file_notes API returns ascending.
  for (const n of notes || []) {
    if (n.authorType !== 'system') continue;
    const t = tsOf(n);
    if (t >= we) break; // past the window; any open pause will close at we below
    const body = n.body || '';
    if (PAUSE_BODIES.has(body)) {
      if (openStart === null) openStart = Math.max(t, ws);
    } else if (body === RESUME_BODY) {
      if (openStart !== null) {
        const close = Math.min(t, we);
        if (close > openStart) intervals.push({ start: openStart, end: close });
        openStart = null;
      }
    }
  }
  if (openStart !== null) {
    if (we > openStart) intervals.push({ start: openStart, end: we });
  }
  return intervals;
}

/**
 * Compute the bonus clock for a file.
 *
 * @param {object} file — file row (camelCase: drawnAt, sentToSignAt, pausedTotalMs, status)
 * @param {Array}  notes — system + user notes for this file (ascending by createdAt)
 * @param {object} [opts]
 * @param {number} [opts.now] — clock reference for active files (default Date.now())
 *
 * @returns {{
 *   firstDrawnAt: number | null,
 *   firstSentToSignAt: number | null,
 *   endTs: number | null,
 *   businessMs: number,
 *   businessDays: number,
 *   qualifies: boolean,
 *   source: 'notes' | 'fallback'
 * }}
 *
 * `endTs` is `firstSentToSignAt` when the file has reached sent_to_sign;
 * for still-active files it is `now` (used by the live Overdue card).
 * `qualifies` is true iff businessMs ≤ 3 business days.
 */
export function computeBonusClock(file, notes, opts = {}) {
  const now = typeof opts.now === 'number' ? opts.now : Date.now();

  // Test records (is_test = true, CLAUDE.md §33) are excluded from the
  // bonus clock entirely — it never starts, pauses, or counts for them.
  // Callers (computeMonthStats / computeOverdue) already filter test
  // records out; this early return is a defensive belt-and-braces so no
  // future caller can accidentally clock a test file.
  if (file?.isTest === true) {
    return {
      firstDrawnAt: null,
      firstSentToSignAt: null,
      endTs: null,
      businessMs: 0,
      businessDays: 0,
      qualifies: false,
      source: 'notes',
    };
  }

  const systemNotes = (notes || []).filter((n) => n.authorType === 'system');
  const hasSystemNotes = systemNotes.length > 0;

  // Endpoints — prefer system-note timestamps; fall back to columns.
  const drawNote = firstSystemNote(systemNotes, (b) => b.startsWith(DRAW_PREFIX));
  const sentNote = firstSystemNote(systemNotes, (b) => b === SENT_TO_SIGN_BODY);

  const firstDrawnAt = drawNote ? tsOf(drawNote) : (file?.drawnAt ? new Date(file.drawnAt).getTime() : null);
  const firstSentToSignAt = sentNote ? tsOf(sentNote) : (file?.sentToSignAt ? new Date(file.sentToSignAt).getTime() : null);

  if (firstDrawnAt === null) {
    return {
      firstDrawnAt: null,
      firstSentToSignAt,
      endTs: null,
      businessMs: 0,
      businessDays: 0,
      qualifies: false,
      source: hasSystemNotes ? 'notes' : 'fallback',
    };
  }

  const endTs = firstSentToSignAt !== null ? firstSentToSignAt : now;
  if (endTs <= firstDrawnAt) {
    return {
      firstDrawnAt,
      firstSentToSignAt,
      endTs,
      businessMs: 0,
      businessDays: 0,
      qualifies: true,
      source: hasSystemNotes ? 'notes' : 'fallback',
    };
  }

  let businessMs;
  if (hasSystemNotes) {
    const grossMs = businessMillisBetween(firstDrawnAt, endTs);
    const intervals = getPauseIntervals(systemNotes, firstDrawnAt, endTs);
    let pausedBusinessMs = 0;
    for (const iv of intervals) {
      pausedBusinessMs += businessMillisBetween(iv.start, iv.end);
    }
    businessMs = Math.max(0, grossMs - pausedBusinessMs);
  } else {
    // Legacy fallback: no system-note timeline available. Approximate
    // paused-business-time as the column's wall-clock pausedTotalMs.
    // This slightly overcounts pauses that straddle weekends, which can
    // only push a file from "qualifies" toward "does not" — never the
    // other way, so it's safe.
    const grossMs = businessMillisBetween(firstDrawnAt, endTs);
    const pausedColMs = Number(file?.pausedTotalMs || 0);
    businessMs = Math.max(0, grossMs - pausedColMs);
  }

  return {
    firstDrawnAt,
    firstSentToSignAt,
    endTs,
    businessMs,
    businessDays: businessMs / (24 * 60 * 60 * 1000),
    qualifies: businessMs <= BONUS_CLOCK_THRESHOLD_MS,
    source: hasSystemNotes ? 'notes' : 'fallback',
  };
}
